import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import "./TicketsPage.css";
import CreateTicketForm from "./CreateTicketForm";
import TicketCard from "./TicketCard";
import TicketChat from "./TicketChat";
import { fetchStoresAPI } from "../../../utils/api/stores";
import {
  fetchTeamTicketAPI,
  fetchTeamUsersAPI,
  fetchTicketsAPI,
  fetchTicketMessageSummaryAPI,
} from "../../../utils/api/teamTicket";
import { Context } from "../../../context/Context";
import { useAccess } from "../../../access/hooks/useAccess";
import { Button, CircleX, Input, NoticeBox, Pencil } from "../../ui";
import FilterGroup from "../../analysis/filter/FilterUI";
import TicketStatusSummary from "./TicketStatusSummary";
import { TICKET_STAGES, TICKET_STATUSES } from "./ticketConstants";
import { ticketMessagesGroupByMonthDays, ticketSummaryGroupByDays } from "../../../utils/MainCharts";
import { FEATURE_NAMES, isFeatureEnabled } from "../../../utils/features";
import { getTicketText } from "./ticketText";
import { LineChart } from "@mui/x-charts";

const formatCount = (value) => (value == null ? "-" : value.toString());

const CHART_SERIES = [
  { key: "totalData", label: "Total", color: "var(--primary-color)" },
  { key: "newData", label: "new", color: "var(--secondary-color)" },
  { key: "completedData", label: "completed", color: "var(--third-color)" },
  { key: "inProgressData", label: "inprogress", color: "var(--forth-color)" },
  { key: "rejectedData", label: "rejected", color: "var(--error)" },
  { key: "incompleteDataData", label: "incomplete", color: "var(--forth-color-trs)" },
  { key: "pendingData", label: "pending", color: "var(--secondary-dark-text-color)" },
  { key: "supportData", label: "support", color: "var(--primary-color)" },
];

const readToken = (contextRef) => {
  const token = localStorage.getItem("auth-token");
  if (!token) contextRef.current.handleTokenExpiration();
  return token;
};

const EMPTY_FILTERS = {
  title: "",
  date: "",
  store: [],
  users: [],
  status: [],
  processingStage: [],
};

// The side panel shows either the create/edit form or a ticket's chat.
const PANEL_NONE = null;
const PANEL_FORM = "form";
const PANEL_CHAT = "chat";

export default function TicketsPage() {
  const { lang, handleTokenExpiration, storeAccess } = useContext(Context);
  const { can } = useAccess();
  // Only feeds the ticket-message chart.
  const canViewAnalytics = can("onboarding.ticket.analytics");
  // TICKET_SUMMARY on: the chart reads one month of counts (live + archived history) instead of every message.
  const summaryOn = isFeatureEnabled(FEATURE_NAMES.TICKET_SUMMARY);
  const text = getTicketText(lang);
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [stores, setStores] = useState([]);
  const [ticketMessages, setTicketMessages] = useState([]);
  // idle | loading | ready | error. `key` names the month and users the counts belong to.
  const [summary, setSummary] = useState({ status: "idle", key: null, counts: [], archived: null });
  const [summaryAttempt, setSummaryAttempt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [panel, setPanel] = useState(PANEL_NONE);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  // null while the form is in "create" mode.
  const [editingTicket, setEditingTicket] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  const panelRef = useRef(null);
  const ticketsRequestRef = useRef(0);
  // The context recreates handleTokenExpiration (and a fallback storeAccess
  // array) on every provider render; reading them through a ref keeps the
  // fetchers stable so the initial load runs once.
  const contextRef = useRef({ handleTokenExpiration, storeAccess });
  contextRef.current = { handleTokenExpiration, storeAccess };

  // Returns false when the request failed. Only the latest call may write
  // state, so an older refresh can't overwrite a newer list.
  const fetchTickets = useCallback(async () => {
    const token = readToken(contextRef);
    if (!token) return false;

    const requestId = ++ticketsRequestRef.current;
    const ticketsData = await fetchTeamTicketAPI(token);
    if (requestId !== ticketsRequestRef.current) return true;

    if (ticketsData) {
      setTickets(ticketsData);
      return true;
    }
    return false;
  }, []);

  const fetchUsers = useCallback(async () => {
    const token = readToken(contextRef);
    if (!token) return;
    const usersData = await fetchTeamUsersAPI(token);
    if (usersData) setUsers(usersData);
  }, []);

  const fetchStores = useCallback(async () => {
    const token = readToken(contextRef);
    if (!token) return;
    const storesData = await fetchStoresAPI(token, contextRef.current.storeAccess);
    if (storesData) setStores(storesData);
  }, []);

  // Only feeds the CEO chart, so other roles skip the request.
  const fetchTicketMessages = useCallback(async () => {
    if (!canViewAnalytics || summaryOn) return;
    const token = readToken(contextRef);
    if (!token) return;
    const ticketMessagesData = await fetchTicketsAPI(token);
    if (ticketMessagesData) setTicketMessages(ticketMessagesData.messages || []);
  }, [canViewAnalytics, summaryOn]);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    const [ticketsLoaded] = await Promise.all([
      fetchTickets(),
      fetchUsers(),
      fetchStores(),
      fetchTicketMessages(),
    ]);
    setLoadError(!ticketsLoaded);
    setLoading(false);
  }, [fetchTickets, fetchUsers, fetchStores, fetchTicketMessages]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // On stacked (narrow) layouts the panel renders below the ticket list;
  // bring it into view when it opens so the click has a visible result.
  useEffect(() => {
    if (!panel || !panelRef.current) return;
    if (!window.matchMedia?.("(max-width: 1024px)").matches) return;
    panelRef.current.scrollIntoView?.({ behavior: "smooth", block: "start" });
  }, [panel, selectedTicketId, editingTicket]);

  const handleFilterChange = (key, values) => {
    setFilters((prev) => ({ ...prev, [key]: values }));
  };

  const toggleCreateForm = () => {
    if (panel === PANEL_FORM) {
      setPanel(PANEL_NONE);
      return;
    }
    // Always a fresh "create" form, never the last edited ticket.
    setEditingTicket(null);
    setPanel(PANEL_FORM);
  };

  const openTicket = (ticket) => {
    setSelectedTicketId(ticket._id);
    setPanel(PANEL_CHAT);
  };

  const editTicket = (ticket) => {
    setEditingTicket(ticket);
    setPanel(PANEL_FORM);
  };

  const closePanel = () => setPanel(PANEL_NONE);

  // Don't leave the chat or edit form open on a ticket that no longer exists.
  const handleTicketDeleted = (ticketId) => {
    if (
      (panel === PANEL_CHAT && selectedTicketId === ticketId) ||
      (panel === PANEL_FORM && editingTicket?._id === ticketId)
    ) {
      setPanel(PANEL_NONE);
    }
    fetchTickets();
  };

  const filteredTickets = useMemo(() => {
    const title = filters.title.toLowerCase();
    return tickets.filter((t) => {
      if (title && !(t.title || "").toLowerCase().includes(title)) return false;

      if (
        filters.store.length > 0 &&
        !filters.store.includes(t.storeId?.businessName)
      )
        return false;

      if (
        filters.users.length > 0 &&
        !filters.users.includes(t.createdBy?.name) &&
        !filters.users.includes(t.assignedTo?.name)
      )
        return false;

      if (filters.status.length > 0 && !filters.status.includes(t.status))
        return false;

      if (
        filters.processingStage.length > 0 &&
        !filters.processingStage.includes(t.processingStage)
      )
        return false;

      return true;
    });
  }, [tickets, filters]);

  // The chart shows the month picked in the date filter (current month by default).
  const chartMonth = useMemo(() => {
    if (!filters.date) return new Date();
    const [year, month] = filters.date.split("-").map(Number);
    return new Date(year, month - 1, 1);
  }, [filters.date]);

  // Ids of the selected users, as the server filters by sender. A selection that matches no loaded user means "nobody".
  const selectedUserIds = useMemo(
    () => users.filter((user) => filters.users.includes(user.name)).map((user) => user._id).sort(),
    [users, filters.users],
  );
  const monthKey = `${chartMonth.getFullYear()}-${String(chartMonth.getMonth() + 1).padStart(2, "0")}`;
  const senderKey = selectedUserIds.join(",");
  const nobodySelected = filters.users.length > 0 && selectedUserIds.length === 0;

  useEffect(() => {
    if (!canViewAnalytics || !summaryOn) return undefined;
    if (nobodySelected) {
      setSummary({ status: "ready", key: `${monthKey}|none`, counts: [], archived: null });
      return undefined;
    }
    const token = localStorage.getItem("auth-token");
    if (!token) {
      contextRef.current.handleTokenExpiration();
      return undefined;
    }
    const controller = new AbortController();
    // The previous answer stays on screen only while it belongs to the same month and users (a retry); otherwise it is cleared.
    const key = `${monthKey}|${senderKey}`;
    setSummary((previous) => ({ status: "loading", key, counts: previous.key === key ? previous.counts : [], archived: previous.key === key ? previous.archived : null }));
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    fetchTicketMessageSummaryAPI(token, {
      month: monthKey,
      tz: timeZone,
      senderIds: senderKey ? senderKey.split(",") : [],
      signal: controller.signal,
    }).then((result) => {
      if (result?.aborted || controller.signal.aborted) return;
      if (result?.ok && Array.isArray(result.data?.counts)) {
        setSummary({ status: "ready", key, counts: result.data.counts, archived: result.data.archived || null });
      } else {
        // No fallback to the unbounded list: show the error and let the user retry.
        setSummary((previous) => ({ ...previous, status: "error" }));
      }
    });
    return () => controller.abort();
  }, [canViewAnalytics, summaryOn, nobodySelected, monthKey, senderKey, summaryAttempt]);

  const chartData = useMemo(() => {
    if (!canViewAnalytics) return null;
    if (summaryOn) return ticketSummaryGroupByDays(summary.counts, chartMonth);

    const selectedUserIds = new Set(
      users
        .filter((user) => filters.users.includes(user.name))
        .map((user) => user._id),
    );
    const start = new Date(chartMonth.getFullYear(), chartMonth.getMonth(), 1);
    const end = new Date(chartMonth.getFullYear(), chartMonth.getMonth() + 1, 1);

    const messages = ticketMessages.filter((message) => {
      if (!message.createdAt) return false;
      if (filters.date) {
        const createdAt = new Date(message.createdAt);
        if (createdAt < start || createdAt >= end) return false;
      }
      if (filters.users.length > 0 && !selectedUserIds.has(message.senderId))
        return false;
      return true;
    });

    return ticketMessagesGroupByMonthDays(messages, chartMonth);
  }, [canViewAnalytics, summaryOn, summary.counts, ticketMessages, users, filters.date, filters.users, chartMonth]);

  const summaryStatus = summaryOn ? summary.status : "idle";
  const chartIsEmpty = summaryOn && summary.status === "ready" && summary.counts.length === 0;

  const hasActiveFilters =
    filters.title ||
    filters.store.length ||
    filters.users.length ||
    filters.status.length ||
    filters.processingStage.length;

  let listContent;
  if (loading && tickets.length === 0) {
    listContent = (
      <p className="tickets-list-status" role="status">
        Loading tickets…
      </p>
    );
  } else if (loadError && tickets.length === 0) {
    listContent = (
      <div role="alert">
        <NoticeBox variant="danger" title="Tickets could not be loaded">
          <Button size="sm" appearance="soft" onClick={loadAll}>
            Retry
          </Button>
        </NoticeBox>
      </div>
    );
  } else if (filteredTickets.length === 0) {
    listContent = (
      <p className="tickets-list-status">
        {hasActiveFilters
          ? "No tickets match the current filters."
          : "No tickets yet."}
      </p>
    );
  } else {
    listContent = filteredTickets.map((ticket) => (
      <TicketCard
        key={ticket._id}
        ticket={ticket}
        isSelected={panel === PANEL_CHAT && selectedTicketId === ticket._id}
        onClick={() => openTicket(ticket)}
        onEdit={() => editTicket(ticket)}
        onDeleted={() => handleTicketDeleted(ticket._id)}
      />
    ));
  }

  return (
    <>
      <div className="filters ticket-filters">
        <Input
          className="report-monthly-text-input"
          type="text"
          placeholder="Filter by title..."
          aria-label="Filter by title"
          value={filters.title}
          onChange={(e) => handleFilterChange("title", e.target.value)}
        />
        <FilterGroup
          label="Store"
          options={stores.map((s) => s.businessName)}
          selected={filters.store}
          onChange={(vals) => handleFilterChange("store", vals)}
        />
        <FilterGroup
          label="Users"
          options={users.map((s) => s.name)}
          selected={filters.users}
          onChange={(vals) => handleFilterChange("users", vals)}
        />
        <FilterGroup
          label="Status"
          options={TICKET_STATUSES}
          selected={filters.status}
          onChange={(vals) => handleFilterChange("status", vals)}
        />
        <FilterGroup
          label="Stages"
          options={TICKET_STAGES}
          selected={filters.processingStage}
          onChange={(vals) => handleFilterChange("processingStage", vals)}
        />
        <Input
          type="month"
          fullWidth={false}
          aria-label="Month"
          value={filters.date || ""}
          onChange={(e) => handleFilterChange("date", e.target.value)}
          className="report-monthly-text-input"
        />
      </div>
      <TicketStatusSummary tickets={filteredTickets} />
      <div className="tickets-page">
        <div className="tickets-sidebar">
          <div className="menu-header-row">
            <Button
              variant={panel === PANEL_FORM ? "danger" : "primary"}
              appearance="soft"
              rounded
              icon={panel === PANEL_FORM ? <CircleX /> : <Pencil />}
              aria-label={panel === PANEL_FORM ? "Close form" : "Create ticket"}
              aria-expanded={panel === PANEL_FORM}
              title={panel === PANEL_FORM ? "Close form" : "Create ticket"}
              onClick={toggleCreateForm}
            />
            {!loading && (
              <span className="tickets-count">
                {filteredTickets.length} / {tickets.length}
              </span>
            )}
          </div>

          <div className="tickets-list" aria-busy={loading || undefined}>
            {listContent}
          </div>
        </div>

        {panel === PANEL_FORM && (
          <div className="tickets-chat" ref={panelRef}>
            <CreateTicketForm
              key={editingTicket?._id ?? "new"}
              users={users}
              stores={stores}
              onClose={closePanel}
              onCreated={fetchTickets}
              lang={lang}
              handleTokenExpiration={handleTokenExpiration}
              isUpdate={!!editingTicket}
              initialData={editingTicket}
            />
          </div>
        )}
        {panel === PANEL_CHAT && selectedTicketId && (
          <div className="tickets-chat" ref={panelRef}>
            <div className="menu-header-row">
              <Button
                variant="danger"
                appearance="soft"
                rounded
                icon={<CircleX />}
                className="guidlines-add-btn"
                aria-label="Close ticket"
                title="Close ticket"
                onClick={closePanel}
              />
            </div>
            <TicketChat
              key={selectedTicketId}
              ticketId={selectedTicketId}
              refresh={fetchTickets}
            />
          </div>
        )}
      </div>
      {summaryOn && canViewAnalytics && summaryStatus === "loading" && (
        <p className="tickets-list-status" role="status">
          {text.chartLoading}
        </p>
      )}
      {summaryOn && canViewAnalytics && summaryStatus === "error" && (
        <div role="alert">
          <NoticeBox variant="danger" title={text.chartError}>
            <Button size="sm" appearance="soft" onClick={() => setSummaryAttempt((n) => n + 1)}>
              {text.retry}
            </Button>
          </NoticeBox>
        </div>
      )}
      {summaryOn && canViewAnalytics && summary.archived?.error && (
        <p className="tickets-list-status" role="status">
          {text.chartArchiveUnavailable}
        </p>
      )}
      {chartIsEmpty && (
        <p className="tickets-list-status" role="status">
          {text.chartEmpty}
        </p>
      )}
      {chartData && summaryStatus !== "error" && (
        <div className="tickets-chart">
          <LineChart
            margin={{ left: -30, bottom: -5 }}
            skipAnimation={false}
            yAxis={[{ min: 0 }]}
            xAxis={[
              {
                data: chartData.names,
                scaleType: "band",
                tickLabelInterval: (value, index) => index % 3 === 0,
              },
            ]}
            series={CHART_SERIES.map(({ key, label, color }) => ({
              data: chartData[key],
              label,
              color,
              valueFormatter: formatCount,
            }))}
            height={300}
          />
        </div>
      )}
    </>
  );
}
